/**
 * ProductionTrackerTab — Discovery → Production Tracker.
 *
 * The tracker is a DOCUMENT, not a dashboard: one paper-like Sheet per
 * producing party (opposing → client → third party), organized BY SUBJECT
 * (what was demanded — accounts merged across rounds by succession chain)
 * with a secondary "By request" view (per-RFP, the firm's classic layout).
 * Everything derived — MISSING intervals, round Bates accounting, link
 * resolution, the DATE event stream — is server-computed on every GET
 * (useProductionTracker); rows persist only what a hand entered.
 *
 * ONE SCROLL CONTEXT: no overflow, no maxHeight, no sticky anywhere in this
 * tab — the sheets are as tall as their content and the DiscoverySection
 * wrapper scrolls the page. Print is the page itself: window.print() with
 * every screen-only affordance tagged data-print="hide" (index.css hides
 * them under @media print), so the sheets print stacked as documents.
 */
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronRight,
  Download,
  Pencil,
  Plus,
  Printer,
  Trash2,
  X,
} from 'lucide-react';
import {
  Button,
  Card,
  ConfirmModal,
  DateText,
  Drawer,
  EmptyState,
  IconButton,
  Input,
  Modal,
  Select,
  Sheet,
  SheetCell,
  SheetHeading,
  SheetRow,
  SheetSection,
  SheetTable,
  Skeleton,
  SkeletonTable,
  Tag,
  TextArea,
  Toolbar,
  ToolbarChip,
} from '../ui';
import type { SelectOption, SheetColumn } from '../ui';
import api, { extractAxiosDetail } from '../../lib/api';
import { partyLabel } from '../../lib/enums';
import { useMissingStatementGaps } from '../../hooks/useDemandLetters';
import type { StatementGap } from '../../types';
import {
  useAccounts,
  type AccountData,
  type AccountSuccessionInfo,
} from '../../hooks/useStatements';
import { useUpdateCase } from '../../hooks/useCases';
import {
  useAssignDocumentRound,
  useCreateItem,
  useCreateRound,
  useCreateSuccession,
  useDeleteItem,
  useDeleteRound,
  useDeleteSuccession,
  usePatchItem,
  usePatchRound,
  useProductionTracker,
  useSetItemAccounts,
  useSetItemDocuments,
} from '../../hooks/useProductionTracker';
import type {
  ItemPatchBody,
  ProducingParty,
  ProductionTracker,
  TrackerAccountLink,
  TrackerChain,
  TrackerItem,
  TrackerItemEvent,
  TrackerItemMissing,
  TrackerParty,
  TrackerRound,
} from '../../hooks/useProductionTracker';

// ── vocab ────────────────────────────────────────────────────────────────────

const PARTY_ORDER: ProducingParty[] = ['opposing', 'client', 'third_party'];

const RESPONSE_STATE_OPTIONS: SelectOption[] = [
  { value: '', label: 'Unanswered' },
  { value: 'produced', label: 'Produced' },
  { value: 'none', label: 'None produced' },
  { value: 'will_supplement', label: 'Will supplement' },
  { value: 'refer', label: 'Refer' },
  { value: 'inspection', label: 'For inspection' },
  { value: 'account_closed', label: 'Account closed' },
];

const METHOD_LABELS: Record<string, string> = {
  rfp_response: 'RFP response',
  subpoena_return: 'Subpoena return',
  informal: 'Informal',
  disclosure: 'Disclosure',
};
const METHOD_OPTIONS: SelectOption[] = Object.entries(METHOD_LABELS).map(
  ([value, label]) => ({ value, label }),
);

const REASON_LABELS: Record<string, string> = {
  reissue: 'Reissued',
  acquisition: 'Bank acquisition',
  conversion: 'Conversion',
};
const REASON_OPTIONS: SelectOption[] = [
  { value: 'reissue', label: 'Reissue (new card/account number)' },
  { value: 'acquisition', label: 'Bank acquisition' },
  { value: 'conversion', label: 'Account conversion' },
];

function ordinal(n: number): string {
  const v = n % 100;
  if (v >= 11 && v <= 13) return `${n}th`;
  const s = ['th', 'st', 'nd', 'rd'][n % 10] ?? 'th';
  return `${n}${s}`;
}

function suppLabel(n: number): string {
  return n === 0 ? 'Original response' : `${ordinal(n)} supp`;
}

/** Chip-sized account handle: trailing 4 digits when the label carries them. */
function shortLabel(label: string | null): string {
  if (!label) return '—';
  const m = label.match(/(\d{4})(?!.*\d)/);
  return m ? `x${m[1]}` : label;
}

function roundTitle(round: TrackerRound): string {
  return round.label || `RFP ${round.round_number}`;
}

// ── month-grain gap runs (detector-backed MISSING for unlinked accounts) ─────

const MONTH_ABBR = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

/** "YYYY-MM-…" (first-of-month ISO) → absolute month index. String-split
 * parsing, never `new Date(iso)` (UTC shift moves dates on non-UTC machines). */
function monthIdx(iso: string): number | null {
  const [y, m] = iso.slice(0, 10).split('-');
  const yi = Number(y);
  const mi = Number(m);
  if (!yi || !mi || mi < 1 || mi > 12) return null;
  return yi * 12 + (mi - 1);
}

function monthText(idx: number): string {
  return `${MONTH_ABBR[idx % 12]} ${Math.floor(idx / 12)}`;
}

/** Consecutive month-grain runs, firm phrasing: "Jan 2020 – Nov 2024,
 * Feb – Jun 2023" (en dash U+2013; same-year runs elide the first year). A
 * trailing gap appends "{first trailing month} – current", merging with a
 * contiguous preceding run. */
function formatMonthRuns(months: string[], trailingFirst: string | null): string {
  const idxs = Array.from(
    new Set(months.map(monthIdx).filter((n): n is number => n !== null)),
  ).sort((a, b) => a - b);
  const runs: [number, number][] = [];
  for (const i of idxs) {
    const last = runs[runs.length - 1];
    if (last && i <= last[1] + 1) last[1] = Math.max(last[1], i);
    else runs.push([i, i]);
  }
  const tIdx = trailingFirst ? monthIdx(trailingFirst) : null;
  let rollingStart: number | null = null;
  if (tIdx !== null) {
    const last = runs[runs.length - 1];
    if (last && last[1] >= tIdx - 1) {
      rollingStart = last[0];
      runs.pop();
    } else {
      rollingStart = tIdx;
    }
  }
  const parts = runs.map(([s, e]) => {
    if (s === e) return monthText(s);
    if (Math.floor(s / 12) === Math.floor(e / 12)) {
      return `${MONTH_ABBR[s % 12]} – ${monthText(e)}`;
    }
    return `${monthText(s)} – ${monthText(e)}`;
  });
  if (rollingStart !== null) parts.push(`${monthText(rollingStart)} – current`);
  return parts.join(', ');
}

/** The firm's shorthand for a response with no produced ranges — response
 * states render as sheet TEXT now (italic cell content), never as chips.
 * refer_to rides both pointer states: 'refer' (where it points) and
 * 'inspection' (where to inspect — "marital residence" on the firm sheet,
 * which renders as the whole cell, the firm's idiom). */
function stateSheetText(
  item: Pick<TrackerItem, 'response_state' | 'refer_to'>,
): string | null {
  const referTo = item.refer_to?.trim();
  switch (item.response_state) {
    case 'produced':
      return 'produced';
    case 'none':
      return 'none';
    case 'will_supplement':
      return 'will suppl.';
    case 'refer':
      return referTo ? `refer to ${referTo}` : 'refer';
    case 'account_closed':
      return 'acct closed';
    case 'inspection':
      return referTo || 'for inspection';
    default:
      return null;
  }
}

const ERROR_BANNER =
  'flex items-start gap-2 rounded-lg border border-danger bg-danger-soft px-3 py-2 text-sm text-danger';

const CANT_READ = "couldn't read that — try '66-93' or 'none'";

/** Local blob download (TrialPackTab pattern — never window.open, which 401s
 * under Clerk because it bypasses the axios auth interceptor). */
async function downloadTrackerBlob(
  url: string,
  params: Record<string, string>,
  filename: string,
) {
  const res = await api.get(url, { responseType: 'blob', params });
  const blobUrl = URL.createObjectURL(
    new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(blobUrl);
}

// ── documents (AttachDocumentsModal substrate — same key/shape as DocumentsTab)

interface TrackerDoc {
  id: number;
  filename: string;
  source_party: ProducingParty | 'joint' | null;
  uploaded_at: string | null;
  bates: { start?: string | null; end?: string | null } | null;
  produced_date: string | null;
  production_supplement: number | null;
  production_round: {
    round_id: number;
    round_number: number;
    label: string | null;
    supplement: number | null;
  } | null;
}

function useCaseDocuments(caseId: string) {
  return useQuery({
    queryKey: ['documents', caseId],
    queryFn: async () => {
      const res = await api.get(`/cases/${caseId}/documents`);
      return res.data.documents as TrackerDoc[];
    },
  });
}

function docBatesText(b: TrackerDoc['bates']): string {
  if (!b?.start) return '';
  return b.end && b.end !== b.start ? `${b.start}–${b.end}` : b.start;
}

// ── commit-on-blur inputs (the ChildSupportTab DraftInput pattern) ───────────

function DraftInput({
  value,
  onCommit,
  type = 'text',
  ...rest
}: {
  value: string | number | null | undefined;
  onCommit: (raw: string) => void;
  type?: string;
} & Omit<React.ComponentProps<typeof Input>, 'value' | 'onChange' | 'type'>) {
  const external = value == null ? '' : String(value);
  const [draft, setDraft] = useState(external);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate draft↔server sync; never yank a value mid-edit
    if (!focused) setDraft(external);
  }, [external, focused]);

  return (
    <Input
      {...rest}
      type={type}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== external) onCommit(draft);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') {
          setDraft(external);
          (e.target as HTMLInputElement).blur();
        }
      }}
    />
  );
}

function DraftTextArea({
  value,
  onCommit,
  ...rest
}: {
  value: string | null | undefined;
  onCommit: (raw: string) => void;
} & Omit<React.ComponentProps<typeof TextArea>, 'value' | 'onChange'>) {
  const external = value ?? '';
  const [draft, setDraft] = useState(external);
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- deliberate draft↔server sync; never yank a value mid-edit
    if (!focused) setDraft(external);
  }, [external, focused]);

  return (
    <TextArea
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        if (draft !== external) onCommit(draft);
      }}
    />
  );
}

// ── typed Bates entries editor (PATCH bates_ranges full-replace) ─────────────

interface RangeDraft {
  start: string;
  end: string;
  date: string;
  supplement: string;
}

/** Draft rows in the server's canonical shape (trimmed, end==start collapsed
 * to '', blank rows dropped) — comparing THIS against the external rows is
 * what keeps `dirty` honest: a typed End equal to Start (single-page
 * production) or trailing whitespace must not read as unsaved forever. */
function canonRangeDrafts(rows: RangeDraft[]): RangeDraft[] {
  return rows
    .map((r) => {
      const start = r.start.trim();
      const end = r.end.trim();
      return {
        start,
        end: end === start ? '' : end,
        date: r.date,
        supplement: String(Number(r.supplement) || 0),
      };
    })
    .filter((r) => r.start || r.end);
}

function BatesRangesEditor({
  caseId,
  item,
  maxSupplement,
}: {
  caseId: string;
  item: TrackerItem;
  maxSupplement: number;
}) {
  const patchItem = usePatchItem(caseId);
  const [err, setErr] = useState<string | null>(null);
  const external: RangeDraft[] = useMemo(
    () =>
      (item.bates_ranges ?? []).map((r) => ({
        start: r.start ?? '',
        end: r.end === r.start ? '' : (r.end ?? ''),
        date: r.date ?? '',
        supplement: String(r.supplement ?? 0),
      })),
    [item.bates_ranges],
  );
  const [rows, setRows] = useState<RangeDraft[]>(external);
  // Dirty over CANONICAL forms: end==start collapses server-side, so raw
  // string comparison would leave the Save button phantom-enabled forever.
  const dirty =
    JSON.stringify(canonRangeDrafts(rows)) !== JSON.stringify(external);

  // Resync drafts when the server rows change (save refetch, ingest stamping,
  // another tab) — but never yank real in-progress edits.
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const externalKey = JSON.stringify(external);
  useEffect(() => {
    // Deliberate draft↔server sync; guarded so it never yanks unsaved edits.
    if (!dirtyRef.current) setRows(external);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- external is memoized off item.bates_ranges; the key IS the dependency
  }, [externalKey]);

  const suppOptions: SelectOption[] = [];
  for (let n = 0; n <= Math.max(maxSupplement + 1, 3); n += 1) {
    suppOptions.push({ value: String(n), label: suppLabel(n) });
  }

  const setRow = (i: number, patch: Partial<RangeDraft>) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const save = () => {
    const cleaned = rows.filter((r) => r.start.trim() || r.end.trim());
    patchItem.mutate(
      {
        itemId: item.id,
        body: {
          bates_ranges: cleaned.map((r) => ({
            start: r.start.trim(),
            end: r.end.trim() || r.start.trim(),
            date: r.date || null,
            supplement: Number(r.supplement) || 0,
            note: null,
          })),
        },
      },
      { onSuccess: () => setErr(null), onError: (e) => setErr(extractAxiosDetail(e)) },
    );
  };

  return (
    <div className="space-y-2">
      <h4 className="t-label text-ink-soft">Bates entries (as answered)</h4>
      {rows.map((row, i) => (
        <div key={i} className="flex flex-wrap items-center gap-2">
          <div className="w-32">
            <Input
              size="sm"
              className="t-ident"
              placeholder="Start"
              aria-label="Bates start"
              value={row.start}
              onChange={(e) => setRow(i, { start: e.target.value })}
            />
          </div>
          <div className="w-32">
            <Input
              size="sm"
              className="t-ident"
              placeholder="End"
              aria-label="Bates end"
              value={row.end}
              onChange={(e) => setRow(i, { end: e.target.value })}
            />
          </div>
          <div className="w-40">
            <Input
              size="sm"
              type="date"
              aria-label="Produced date"
              value={row.date}
              onChange={(e) => setRow(i, { date: e.target.value })}
            />
          </div>
          <div className="w-40">
            <Select
              size="sm"
              aria-label="Supplement"
              options={suppOptions}
              value={row.supplement}
              onChange={(e) => setRow(i, { supplement: e.target.value })}
            />
          </div>
          <IconButton
            icon={X}
            aria-label="Remove Bates entry"
            size="xs"
            onClick={() => setRows((prev) => prev.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="ghost"
          icon={Plus}
          onClick={() =>
            setRows((prev) => [...prev, { start: '', end: '', date: '', supplement: '0' }])
          }
        >
          Add range
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={!dirty}
          loading={patchItem.isPending}
          onClick={save}
        >
          Save Bates entries
        </Button>
      </div>
      {err && <div className={ERROR_BANNER}>{err}</div>}
    </div>
  );
}

// ── AttachDocumentsModal ─────────────────────────────────────────────────────
// Round mode: check/uncheck assigns documents to (or releases them from) the
// round via the per-document PATCH. Item mode: newly checked round-less files
// are PATCHed into the round first, then the item's document set is replaced.

function AttachDocumentsModal({
  caseId,
  round,
  party,
  partyName,
  itemId,
  itemDocIds,
  onClose,
}: {
  caseId: string;
  round: TrackerRound;
  party: ProducingParty;
  partyName: string;
  itemId?: number;
  itemDocIds?: number[];
  onClose: () => void;
}) {
  const docsQ = useCaseDocuments(caseId);
  const assignRound = useAssignDocumentRound(caseId);
  const setItemDocs = useSetItemDocuments(caseId);
  const [overrides, setOverrides] = useState<Map<number, boolean>>(new Map());
  const [suppSel, setSuppSel] = useState('0');
  const [producedDate, setProducedDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const itemMode = itemId !== undefined;
  const eligible = useMemo(
    () =>
      (docsQ.data ?? []).filter(
        (d) =>
          d.source_party === party &&
          (!d.production_round || d.production_round.round_id === round.id),
      ),
    [docsQ.data, party, round.id],
  );

  const defaultChecked = (doc: TrackerDoc) =>
    itemMode
      ? (itemDocIds ?? []).includes(doc.id)
      : doc.production_round?.round_id === round.id;
  const isChecked = (doc: TrackerDoc) => overrides.get(doc.id) ?? defaultChecked(doc);

  const maxSupplement = round.supplements.reduce((m, s) => Math.max(m, s.supplement), 0);
  const suppOptions: SelectOption[] = [
    { value: '0', label: 'Original response' },
    ...round.supplements.map((s) => ({
      value: String(s.supplement),
      label: suppLabel(s.supplement),
    })),
    { value: 'new', label: `New supplement… (${ordinal(maxSupplement + 1)})` },
  ];

  const save = async () => {
    setBusy(true);
    setErr(null);
    const suppNumber = suppSel === 'new' ? maxSupplement + 1 : Number(suppSel) || 0;
    const assignBody = {
      round_id: round.id,
      supplement: suppNumber > 0 ? suppNumber : null,
      produced_date: producedDate || null,
    };
    try {
      if (itemMode) {
        for (const doc of eligible) {
          if (isChecked(doc) && !doc.production_round) {
            await assignRound.mutateAsync({ documentId: doc.id, body: assignBody });
          }
        }
        await setItemDocs.mutateAsync({
          itemId: itemId as number,
          documentIds: eligible.filter(isChecked).map((d) => d.id),
        });
      } else {
        for (const doc of eligible) {
          const was = doc.production_round?.round_id === round.id;
          const now = isChecked(doc);
          if (now && !was) {
            await assignRound.mutateAsync({ documentId: doc.id, body: assignBody });
          } else if (!now && was) {
            await assignRound.mutateAsync({ documentId: doc.id, body: { round_id: null } });
          }
        }
      }
      onClose();
    } catch (e) {
      setErr(extractAxiosDetail(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={itemMode ? 'Attach documents to item' : `Assign documents — ${roundTitle(round)}`}
      description={
        itemMode
          ? 'Link produced files to this register row. Round-less files are assigned to the round first.'
          : `Files produced by ${partyName}. Checking assigns a file to this round; unchecking releases it.`
      }
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={save}>
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56">
            <Select
              label="Arrived as"
              size="sm"
              options={suppOptions}
              value={suppSel}
              onChange={(e) => setSuppSel(e.target.value)}
            />
          </div>
          <div className="w-44">
            <Input
              label="Produced date"
              size="sm"
              type="date"
              value={producedDate}
              onChange={(e) => setProducedDate(e.target.value)}
              hint="Blank = today"
            />
          </div>
        </div>
        {docsQ.isLoading ? (
          <Skeleton variant="rect" width="100%" height={96} />
        ) : eligible.length === 0 ? (
          <EmptyState
            size="inline"
            title={`No documents from ${partyName}`}
            description="Upload this party's productions on the Upload tab, then assign them here."
          />
        ) : (
          <ul className="divide-y divide-border-subtle">
            {eligible.map((doc) => (
              <li key={doc.id} className="flex items-center gap-3 py-2">
                <label className="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
                  {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                  <input
                    type="checkbox"
                    className="size-4 shrink-0 accent-green-700"
                    checked={isChecked(doc)}
                    onChange={(e) =>
                      setOverrides((prev) => new Map(prev).set(doc.id, e.target.checked))
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-sm">{doc.filename}</span>
                </label>
                {doc.uploaded_at && (
                  <span className="t-caption shrink-0 text-ink-faint">
                    <DateText value={doc.uploaded_at} />
                  </span>
                )}
                {doc.production_round && (
                  <Tag tone="neutral" size="sm">
                    {suppLabel(doc.production_round.supplement ?? 0)}
                  </Tag>
                )}
                {docBatesText(doc.bates) && (
                  <Tag tone="neutral" size="sm">
                    <span className="t-ident">{docBatesText(doc.bates)}</span>
                  </Tag>
                )}
              </li>
            ))}
          </ul>
        )}
        {err && <div className={ERROR_BANNER}>{err}</div>}
      </div>
    </Modal>
  );
}

// ── RoundModal (create / edit) ───────────────────────────────────────────────

function RoundModal({
  caseId,
  party,
  partyName,
  partyRoundCount,
  round,
  onClose,
}: {
  caseId: string;
  party: ProducingParty;
  partyName: string;
  partyRoundCount: number;
  round?: TrackerRound;
  onClose: () => void;
}) {
  const createRound = useCreateRound(caseId);
  const patchRound = usePatchRound(caseId);
  const editing = !!round;
  const [method, setMethod] = useState(round?.method ?? 'rfp_response');
  const [label, setLabel] = useState(round?.label ?? '');
  const [served, setServed] = useState(round?.served_date ?? '');
  const [due, setDue] = useState(round?.response_due_date ?? '');
  const [responded, setResponded] = useState(round?.response_date ?? '');
  const [notes, setNotes] = useState(round?.notes ?? '');
  const [seed, setSeed] = useState(!editing && partyRoundCount === 0);
  const [err, setErr] = useState<string | null>(null);

  const submit = () => {
    const shared = {
      method,
      label: label.trim() || null,
      served_date: served || null,
      response_due_date: due || null,
      response_date: responded || null,
    };
    if (editing) {
      patchRound.mutate(
        { roundId: round.id, body: { ...shared, notes: notes.trim() || null } },
        { onSuccess: onClose, onError: (e) => setErr(extractAxiosDetail(e)) },
      );
    } else {
      createRound.mutate(
        { producing_party: party, ...shared, seed_taxonomy: seed },
        { onSuccess: onClose, onError: (e) => setErr(extractAxiosDetail(e)) },
      );
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? `Edit ${roundTitle(round)}` : `New round — ${partyName}`}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={createRound.isPending || patchRound.isPending}
            onClick={submit}
          >
            {editing ? 'Save' : 'Add round'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <Select
          label="Method"
          size="sm"
          options={METHOD_OPTIONS}
          value={method}
          onChange={(e) => setMethod(e.target.value)}
        />
        <Input
          label="Label"
          size="sm"
          placeholder={editing ? roundTitle(round) : `RFP ${partyRoundCount + 1}`}
          hint="Optional — blank shows the round number."
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
        <div className="grid grid-cols-3 gap-2">
          <Input
            label="Served"
            size="sm"
            type="date"
            value={served}
            onChange={(e) => setServed(e.target.value)}
          />
          <Input
            label="Due"
            size="sm"
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
          <Input
            label="Responses"
            size="sm"
            type="date"
            value={responded}
            onChange={(e) => setResponded(e.target.value)}
          />
        </div>
        {editing ? (
          <TextArea
            label="Notes (work product)"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        ) : (
          <label className="flex cursor-pointer items-center gap-2">
            {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
            <input
              type="checkbox"
              className="size-4 shrink-0 accent-green-700"
              checked={seed}
              onChange={(e) => setSeed(e.target.checked)}
            />
            <span className="text-sm">Seed the standard 33-item RFP set</span>
          </label>
        )}
        {err && <div className={ERROR_BANNER}>{err}</div>}
      </div>
    </Modal>
  );
}

// ── inline BATES quick-edit (both views) ─────────────────────────────────────
// Clicking the BATES/PRODUCED cell text (not the row) opens a small inline
// input; free text is parsed on commit into typed bates_ranges or a response
// state — the firm types "66-93" or "none" straight into the cell.

type QuickParse = { body: ItemPatchBody } | { error: string } | null;

function parseBatesQuickEntry(raw: string, item: TrackerItem): QuickParse {
  const text = raw.trim();
  if (!text) return null; // empty commit = cancel, never a silent clear
  const lower = text.toLowerCase();

  // Keyword responses (the firm's shorthand vocabulary).
  if (lower === 'none' || lower === 'none produced') {
    return { body: { response_state: 'none' } };
  }
  if (lower === 'produced') return { body: { response_state: 'produced' } };
  if (lower.startsWith('will suppl')) {
    return { body: { response_state: 'will_supplement' } };
  }
  if (lower.startsWith('refer to ')) {
    return { body: { response_state: 'refer', refer_to: text.slice(9).trim() || null } };
  }
  if (lower === 'refer') return { body: { response_state: 'refer' } };
  if (lower === 'closed' || lower === 'acct closed' || lower === 'account closed') {
    return { body: { response_state: 'account_closed' } };
  }
  if (lower === 'inspection' || lower === 'for inspection') {
    return { body: { response_state: 'inspection' } };
  }
  if (lower === 'marital residence' || lower === 'martial residence') {
    return { body: { response_state: 'inspection', refer_to: text } };
  }

  // Bates tokens: split on commas/newlines; each `A–B` / `A-B` / `A` with an
  // optional alpha prefix (SMITH000104). Any range present → 'produced'.
  const tokens = text
    .split(/[,\n]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const side = /^[A-Za-z]*\s?\d+$/;
  const entries: { start: string; end: string }[] = [];
  for (const tok of tokens) {
    const sides = tok.split(/\s*[–—-]\s*/).map((s) => s.trim());
    if (sides.length === 0 || sides.length > 2 || !sides.every((s) => side.test(s))) {
      return { error: CANT_READ };
    }
    entries.push({ start: sides[0], end: sides[1] ?? sides[0] });
  }
  if (entries.length === 0) return { error: CANT_READ };
  // Preserve date/supplement of an existing entry with the same start; new
  // entries arrive dateless at supplement 0.
  const prior = new Map((item.bates_ranges ?? []).map((r) => [r.start, r]));
  const bates_ranges = entries.map(({ start, end }) => {
    const p = prior.get(start);
    return { start, end, date: p?.date ?? null, supplement: p?.supplement ?? 0, note: null };
  });
  return { body: { bates_ranges, response_state: 'produced' } };
}

function quickEditSeed(item: TrackerItem): string {
  return item.bates_display ?? stateSheetText(item) ?? '';
}

function BatesQuickEdit({
  caseId,
  item,
  children,
}: {
  caseId: string;
  item: TrackerItem;
  children: React.ReactNode;
}) {
  const patchItem = usePatchItem(caseId);
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancelled = useRef(false);

  const commit = (raw: string) => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    // Unchanged text (the seed round-tripping through blur) is a no-op close —
    // an inspection location like "storage unit" must not error on plain blur.
    if (raw.trim() === quickEditSeed(item).trim()) {
      setEditing(false);
      setErr(null);
      return;
    }
    const parsed = parseBatesQuickEntry(raw, item);
    if (parsed === null) {
      setEditing(false);
      setErr(null);
      return;
    }
    if ('error' in parsed) {
      setErr(parsed.error);
      return;
    }
    patchItem.mutate(
      { itemId: item.id, body: parsed.body },
      {
        onSuccess: () => {
          setEditing(false);
          setErr(null);
        },
        onError: (e) => setErr(extractAxiosDetail(e)),
      },
    );
  };

  if (!editing) {
    return (
      <div
        className="-my-1 min-h-6 cursor-text py-1"
        title="Click to edit — type '66-93' or 'none'"
        onClick={(e) => {
          e.stopPropagation();
          cancelled.current = false;
          setEditing(true);
        }}
      >
        {children}
      </div>
    );
  }
  return (
    <div onClick={(e) => e.stopPropagation()}>
      <Input
        size="sm"
        className="t-ident"
        aria-label="Bates quick entry"
        autoFocus
        defaultValue={quickEditSeed(item)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') {
            cancelled.current = true;
            setErr(null);
            setEditing(false);
          }
        }}
        onBlur={(e) => commit(e.target.value)}
      />
      {err && <p className="t-caption mt-1 text-danger">{err}</p>}
      {patchItem.isPending && <p className="t-caption mt-1 text-ink-faint">Saving…</p>}
    </div>
  );
}

// ── shared cell content ──────────────────────────────────────────────────────

/** Produced-event lines: date · range · supp ordinal, sorted by date. */
function EventLines({ events, withRanges }: { events: TrackerItemEvent[]; withRanges: boolean }) {
  return (
    <div className="space-y-1">
      {events.map((e, i) => (
        <div key={i}>
          {e.date ? <DateText value={e.date} /> : <span className="text-ink-faint">—</span>}
          {withRanges && e.range && (
            <>
              {' '}
              <span className="t-ident">{e.range}</span>
            </>
          )}
          {e.supplement > 0 && (
            <span className="t-caption text-ink-faint"> {ordinal(e.supplement)} supp</span>
          )}
        </div>
      ))}
    </div>
  );
}

/** The MISSING cell: computed display (deep-links into demand letters when the
 * account last4s are known), the attorney's missing_note demands (served
 * MISSING content — they render HERE, never in work-product notes), and any
 * succession seam notes. */
function MissingCellContent({
  caseId,
  missing,
  missingNotes,
  last4s,
}: {
  caseId: string;
  missing: TrackerItemMissing | null;
  missingNotes: string[];
  last4s: string[];
}) {
  const noteLines = missingNotes.map((n) => (
    <div key={n} className="t-caption italic text-ink-soft">
      {n}
    </div>
  ));
  if (missing?.complete) {
    return (
      <div className="space-y-1">
        <span className="text-ink-faint">complete</span>
        {noteLines}
      </div>
    );
  }
  if (!missing) {
    return missingNotes.length > 0 ? (
      <div className="space-y-1">{noteLines}</div>
    ) : (
      <span className="text-ink-faint">—</span>
    );
  }
  return (
    <div className="space-y-1">
      {last4s.length > 0 ? (
        <Link
          to={`/cases/${caseId}/drafts/letters?focus_last4=${last4s.join(',')}`}
          className="block whitespace-pre-line text-danger hover:underline"
          title="Open in demand letters"
        >
          {missing.display}
        </Link>
      ) : (
        <span className="block whitespace-pre-line text-danger">{missing.display}</span>
      )}
      {noteLines}
      {(missing.seam_notes ?? []).map((n) => (
        <div key={n} className="t-caption text-ink-faint">
          {n}
        </div>
      ))}
    </div>
  );
}

/** The collapsed-rows chevron line (screen-only — hidden in print). */
function CollapsedLine({
  colSpan,
  open,
  onToggle,
  label,
}: {
  colSpan: number;
  open: boolean;
  onToggle: () => void;
  label: string;
}) {
  const Icon = open ? ChevronDown : ChevronRight;
  return (
    <SheetRow data-print="hide">
      <SheetCell colSpan={colSpan} className="border-b-0">
        <Button
          variant="ghost"
          size="sm"
          icon={Icon}
          className="h-auto px-1 py-0 text-ink-soft"
          onClick={onToggle}
        >
          {label}
        </Button>
      </SheetCell>
    </SheetRow>
  );
}

// ── by-subject data mapping ──────────────────────────────────────────────────

interface AccountRowLink {
  round: TrackerRound;
  item: TrackerItem;
  link: TrackerAccountLink;
}

interface AccountRow {
  key: string;
  /** Bank name (chain rows) or the full terminal label (single accounts). */
  titleRoot: string;
  /** Chain member last4 handles joined with → — null for single accounts. */
  titleShorts: string[] | null;
  /** The terminal bank_label — what add-to-request PUTs. */
  bankLabel: string;
  links: AccountRowLink[];
  citations: string[];
  events: TrackerItemEvent[];
  stateText: string | null;
  missing: TrackerItemMissing | null;
  missingNotes: string[];
  last4s: string[];
  /** Unlinked rows: statements-on-file span across chain members. */
  producedSpan: { start: string | null; end: string | null } | null;
  collapsed: boolean;
  daysMissing: number;
}

/** Detector-backed math for an UNLINKED row (no item links → no server-side
 * item MISSING): the case gap feed's month-grain runs, shaped like
 * TrackerItemMissing so the cell renders identically to linked rows. */
interface DetectorInfo {
  missing: TrackerItemMissing | null;
  producedSpan: { start: string | null; end: string | null } | null;
  last4s: string[];
}

/** Month-grain detector gaps for one logical account (chain): gaps are
 * emitted under the chain TERMINAL label with chain_labels — match on both. */
function detectorInfoFor(
  labels: string[],
  members: AccountData[],
  gaps: StatementGap[],
  anchor: string | null,
): DetectorInfo {
  const matched = gaps.filter(
    (g) =>
      labels.includes(g.account_label) ||
      (g.chain_labels ?? []).some((l) => labels.includes(l)),
  );
  // The detectors report gaps across the whole observed range; the sheet only
  // demands from the anchor forward, so pre-anchor months never show as red.
  const anchorMonth = anchor ? `${anchor.slice(0, 7)}-01` : null;
  const monthSet = new Set<string>();
  for (const g of matched) {
    for (const m of g.leading_months ?? []) monthSet.add(m.slice(0, 10));
    for (const m of g.missing_months ?? []) monthSet.add(m.slice(0, 10));
  }
  if (anchorMonth) {
    for (const m of Array.from(monthSet)) if (m < anchorMonth) monthSet.delete(m);
  }
  const trailing = matched.find((g) => g.gap_type === 'trailing_gap');
  const trailingFirst = trailing?.trailing_months?.[0] ?? null;
  const trailingCount = trailing
    ? (trailing.trailing_months?.length ?? trailing.months_stale ?? 1)
    : 0;
  const display = formatMonthRuns(Array.from(monthSet), trailingFirst);
  const monthsMissing = monthSet.size + trailingCount;

  const sortedMonths = Array.from(monthSet).sort();
  const missing: TrackerItemMissing | null = display
    ? {
        window_start: sortedMonths[0] ?? trailingFirst ?? '',
        window_end: trailingFirst ? null : (sortedMonths[sortedMonths.length - 1] ?? null),
        intervals: [],
        display,
        days_missing: monthsMissing * 30,
        complete: false,
      }
    : null;

  const last4s = Array.from(
    new Set([
      ...labels
        .map((l) => l.match(/(\d{4})(?!.*\d)/)?.[1])
        .filter((s): s is string => !!s),
      ...matched.map((g) => g.last4).filter((s): s is string => !!s),
    ]),
  );

  let start: string | null = null;
  let end: string | null = null;
  for (const m of members) {
    if (m.earliest_date && (!start || m.earliest_date < start)) start = m.earliest_date;
    if (m.latest_statement_date && (!end || m.latest_statement_date > end)) {
      end = m.latest_statement_date;
    }
  }
  return {
    missing,
    producedSpan: start || end ? { start, end } : null,
    last4s,
  };
}

/** label → its succession chain. Prefers the aggregate's chains; falls back to
 * the accounts endpoint's succession edges when the aggregate has none. */
function buildChainIndex(
  chains: TrackerChain[],
  successions: AccountSuccessionInfo[],
): Map<string, { terminal: string; labels: string[] }> {
  const idx = new Map<string, { terminal: string; labels: string[] }>();
  for (const c of chains) {
    for (const l of c.labels) idx.set(l, { terminal: c.terminal, labels: c.labels });
  }
  if (idx.size === 0 && successions.length > 0) {
    const next = new Map(successions.map((s) => [s.predecessor_bank_label, s.successor_bank_label]));
    const prev = new Map(successions.map((s) => [s.successor_bank_label, s.predecessor_bank_label]));
    for (const start of new Set([...next.keys(), ...next.values()])) {
      if (idx.has(start)) continue;
      let root = start;
      const up = new Set([root]);
      while (prev.has(root) && !up.has(prev.get(root) as string)) {
        root = prev.get(root) as string;
        up.add(root);
      }
      const labels = [root];
      let cur = root;
      const down = new Set([root]);
      while (next.has(cur) && !down.has(next.get(cur) as string)) {
        cur = next.get(cur) as string;
        labels.push(cur);
        down.add(cur);
      }
      for (const m of labels) idx.set(m, { terminal: cur, labels });
    }
  }
  return idx;
}

function makeAccountRow(
  terminal: string,
  labels: string[],
  links: AccountRowLink[],
  detector?: DetectorInfo,
): AccountRow {
  // Chain rows title as `Regions Checking x7136 → x9249` — one logical account.
  const isChain = labels.length >= 2;
  const titleRoot = isChain
    ? labels[0].replace(/\s*x*\d{4}\s*$/i, '').trim() || labels[0]
    : terminal;
  const titleShorts = isChain ? labels.map(shortLabel) : null;

  const seen = new Set<string>();
  const events: TrackerItemEvent[] = [];
  for (const l of links) {
    for (const e of l.item.events) {
      const k = `${e.date ?? ''}|${e.supplement}|${e.range ?? ''}`;
      if (seen.has(k)) continue;
      seen.add(k);
      events.push(e);
    }
  }
  events.sort((a, b) => (a.date ?? '9999').localeCompare(b.date ?? '9999'));

  const stateItem = links.find((l) => l.item.response_state);
  const stateText = stateItem ? stateSheetText(stateItem.item) : null;

  // Per-link MISSING: multi-account items carry it on the link; a
  // single-account item's own missing IS that account's math. Pick the
  // earliest window_start (tie: max days_missing), preferring windows that
  // still have gaps over already-complete ones. Unlinked rows carry the
  // detector's month-grain math instead — never a silent em dash.
  const candidates: TrackerItemMissing[] = [];
  for (const l of links) {
    const m = l.link.missing ?? (l.item.accounts.length === 1 ? l.item.missing : null);
    if (m) candidates.push(m);
  }
  const pool = candidates.some((m) => !m.complete)
    ? candidates.filter((m) => !m.complete)
    : candidates;
  let missing: TrackerItemMissing | null = null;
  for (const m of pool) {
    if (!missing) {
      missing = m;
      continue;
    }
    const cmp = m.window_start.localeCompare(missing.window_start);
    if (cmp < 0 || (cmp === 0 && m.days_missing > missing.days_missing)) missing = m;
  }
  const linked = links.length > 0;
  if (!linked) missing = detector?.missing ?? null;

  const missingNotes = Array.from(
    new Set(links.map((l) => l.item.missing_note?.trim()).filter((n): n is string => !!n)),
  );
  const citations = links.map((l) => `RFP ${l.round.round_number} #${l.item.item_number}`);
  const last4s = linked
    ? Array.from(
        new Set(
          links.flatMap((l) => l.link.chain_last4s ?? (l.link.last4 ? [l.link.last4] : [])),
        ),
      )
    : (detector?.last4s ?? []);

  // One magnitude key across all rows: linked = the item's day-grain math,
  // unlinked = detector gap months × 30 (baked into the synthesized
  // days_missing). Only genuinely quiet rows fold into the chevron line —
  // (linked AND complete) OR (no gaps, no events, no state).
  const complete = linked && (missing?.complete ?? false);
  const hasGaps = missing !== null && !missing.complete;
  const quiet = !hasGaps && events.length === 0 && !stateText;
  const daysMissing = missing !== null && !missing.complete ? missing.days_missing : 0;
  return {
    key: terminal,
    titleRoot,
    titleShorts,
    bankLabel: terminal,
    links,
    citations,
    events,
    stateText,
    missing,
    missingNotes,
    last4s,
    producedSpan: linked ? null : (detector?.producedSpan ?? null),
    collapsed: (complete || quiet) && missingNotes.length === 0,
    daysMissing,
  };
}

/** The Accounts-section universe, per party sheet: every case account group
 * (they all have statements), merged by succession chain, assigned to the
 * sheet of the party whose items link it. */
function buildAccountRows(
  tracker: ProductionTracker,
  accounts: AccountData[],
  successions: AccountSuccessionInfo[],
  gaps: StatementGap[],
): Map<ProducingParty, AccountRow[]> {
  const chainIdx = buildChainIndex(tracker.chains, successions);
  const memberOf = (label: string) => chainIdx.get(label) ?? { terminal: label, labels: [label] };

  const logical = new Map<
    string,
    { labels: string[]; account: AccountData; members: AccountData[] }
  >();
  for (const acc of accounts) {
    const { terminal, labels } = memberOf(acc.bank_label);
    const existing = logical.get(terminal);
    if (!existing) logical.set(terminal, { labels, account: acc, members: [acc] });
    else {
      existing.members.push(acc);
      if (acc.bank_label === terminal) existing.account = acc;
    }
  }

  const linksByTerminal = new Map<string, Map<ProducingParty, AccountRowLink[]>>();
  for (const party of tracker.parties) {
    for (const round of party.rounds) {
      for (const item of round.items) {
        for (const link of item.accounts) {
          const label = link.current_bank_label ?? link.bank_label;
          if (!label) continue;
          const terminal = memberOf(label).terminal;
          if (!logical.has(terminal)) continue;
          let byParty = linksByTerminal.get(terminal);
          if (!byParty) {
            byParty = new Map();
            linksByTerminal.set(terminal, byParty);
          }
          const arr = byParty.get(party.producing_party) ?? [];
          arr.push({ round, item, link });
          byParty.set(party.producing_party, arr);
        }
      }
    }
  }

  const hasRounds = (p: ProducingParty) =>
    (tracker.parties.find((x) => x.producing_party === p)?.rounds.length ?? 0) > 0;

  const out = new Map<ProducingParty, AccountRow[]>();
  const push = (p: ProducingParty, row: AccountRow) => {
    const arr = out.get(p) ?? [];
    arr.push(row);
    out.set(p, arr);
  };

  for (const [terminal, { labels, account, members }] of logical) {
    const byParty = linksByTerminal.get(terminal);
    if (byParty && byParty.size > 0) {
      for (const [p, links] of byParty) push(p, makeAccountRow(terminal, labels, links));
      continue;
    }
    const detector = detectorInfoFor(labels, members, gaps, tracker.anchor);
    // Unlinked account → ONE sheet, by a deliberately simple rule (do not
    // overfit): its producer's sheet when the documents name one
    // (opposing/third_party); else the opposing sheet only if the account's
    // owner is Opposing/Unknown; else the client sheet when the client has
    // rounds; else opposing. Sheets render only for parties with rounds, so
    // fall back to the first such party.
    let p: ProducingParty;
    if (account.source_party === 'opposing' || account.source_party === 'third_party') {
      p = account.source_party;
    } else {
      const owner = (account.owner_type ?? '').toLowerCase();
      const opposingish = owner.includes('oppos') || owner.includes('unknown') || owner === '';
      p = opposingish ? 'opposing' : hasRounds('client') ? 'client' : 'opposing';
    }
    if (!hasRounds(p)) p = PARTY_ORDER.find(hasRounds) ?? p;
    push(p, makeAccountRow(terminal, labels, [], detector));
  }

  // Problems first (days missing desc), then answered-without-coverage, then
  // the rest; collapsed rows keep their relative order at the end.
  for (const rows of out.values()) {
    rows.sort((a, b) => {
      if (a.collapsed !== b.collapsed) return a.collapsed ? 1 : -1;
      if (a.daysMissing !== b.daysMissing) return b.daysMissing - a.daysMissing;
      return a.key.localeCompare(b.key);
    });
  }
  return out;
}

interface ItemRow {
  round: TrackerRound;
  item: TrackerItem;
}

/** Non-account items for the "Everything else demanded" section. Items with
 * nothing recorded — and quiet "none" answers with no demands — fold into the
 * collapsed line. */
function splitEverythingElse(party: TrackerParty): { rows: ItemRow[]; collapsedRows: ItemRow[] } {
  const rows: ItemRow[] = [];
  const collapsedRows: ItemRow[] = [];
  for (const round of party.rounds) {
    const items = [...round.items].sort((a, b) => a.item_number - b.item_number);
    for (const item of items) {
      if (item.accounts.length > 0) continue;
      const hasGap = !!item.missing && !item.missing.complete && !!item.missing.display;
      const recorded =
        item.response_state !== null ||
        item.events.length > 0 ||
        !!item.missing_note ||
        !!item.demand_start;
      const quietNone =
        item.response_state === 'none' &&
        item.events.length === 0 &&
        !item.missing_note &&
        !item.demand_start &&
        !hasGap;
      if (!recorded || quietNone) collapsedRows.push({ round, item });
      else rows.push({ round, item });
    }
  }
  rows.sort((a, b) => {
    const gap = (r: ItemRow) =>
      r.item.missing && !r.item.missing.complete ? r.item.missing.days_missing : 0;
    return gap(b) - gap(a);
  });
  return { rows, collapsedRows };
}

// ── by-subject sections ──────────────────────────────────────────────────────

const SUBJECT_ACCOUNT_COLUMNS: SheetColumn[] = [
  { key: 'account', label: 'Account' },
  { key: 'produced', label: 'Produced', width: 'w-56' },
  { key: 'missing', label: 'Missing', width: 'w-64' },
];

const SUBJECT_ITEM_COLUMNS: SheetColumn[] = [
  { key: 'item', label: 'Item' },
  { key: 'produced', label: 'Produced', width: 'w-56' },
  { key: 'missing', label: 'Missing', width: 'w-64' },
];

/** "add to request…" — link an unrequested account to one of this party's
 * RFP items (PUT accounts = existing labels + this one). */
function AddToRequestLink({
  caseId,
  party,
  bankLabel,
  label = 'add to request…',
}: {
  caseId: string;
  party: TrackerParty;
  bankLabel: string;
  label?: string;
}) {
  const setItemAccounts = useSetItemAccounts(caseId);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const pick = (item: TrackerItem) => {
    const resolved = item.accounts
      .filter((a) => a.resolved && a.current_bank_label)
      .map((a) => a.current_bank_label as string);
    // Unresolved stored links ride every replace-set PUT — see RowDrawer.
    const unresolved = item.accounts
      .filter((a) => !a.resolved)
      .map((a) => a.bank_label)
      .filter((l): l is string => !!l);
    setItemAccounts.mutate(
      { itemId: item.id, bankLabels: Array.from(new Set([...resolved, ...unresolved, bankLabel])) },
      {
        onSuccess: () => {
          setErr(null);
          setOpen(false);
        },
        onError: (e) => setErr(extractAxiosDetail(e)),
      },
    );
  };

  return (
    <span className="relative inline-flex" data-print="hide">
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-1 py-0 text-ink-soft underline decoration-dashed underline-offset-4"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        {label}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div
            className="absolute left-0 top-full z-20 mt-1 w-80 rounded-overlay border border-border bg-cream-light py-1 shadow-overlay"
            onClick={(e) => e.stopPropagation()}
          >
            {party.rounds.flatMap((round) =>
              [...round.items]
                .sort((a, b) => a.item_number - b.item_number)
                .map((item) => (
                  <Button
                    key={item.id}
                    variant="ghost"
                    size="sm"
                    fullWidth
                    className="justify-start px-3"
                    loading={setItemAccounts.isPending}
                    onClick={() => pick(item)}
                  >
                    <span className="min-w-0 truncate">
                      RFP {round.round_number} #{item.item_number} — {item.description}
                    </span>
                  </Button>
                )),
            )}
            {err && <p className="t-caption px-3 py-1 text-danger">{err}</p>}
          </div>
        </>
      )}
    </span>
  );
}

function AccountsSection({
  caseId,
  party,
  rows,
  onOpenItem,
}: {
  caseId: string;
  party: TrackerParty;
  rows: AccountRow[];
  onOpenItem: (itemId: number) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const visible = rows.filter((r) => !r.collapsed);
  const hidden = rows.filter((r) => r.collapsed);
  const shown = showAll ? [...visible, ...hidden] : visible;
  if (rows.length === 0) return null;

  // A blanket bank-statements item covers unlinked accounts by its terms even
  // before a hand link exists — cite it honestly instead of "not yet requested".
  let blanket: { round: TrackerRound; item: TrackerItem } | null = null;
  for (const round of party.rounds) {
    const item = round.items.find((i) => i.template_key === 'bank_statements');
    if (item) {
      blanket = { round, item };
      break;
    }
  }

  const renderRow = (row: AccountRow) => {
    const firstItem = row.links[0]?.item ?? null;
    const unlinked = row.links.length === 0;
    const produced =
      row.events.length > 0 ? (
        <EventLines events={row.events} withRanges />
      ) : row.stateText ? (
        <span className="italic text-ink-soft">{row.stateText}</span>
      ) : unlinked && row.producedSpan ? (
        <span className="text-ink-soft">
          statements <DateText value={row.producedSpan.start} /> –{' '}
          <DateText value={row.producedSpan.end} /> on file
        </span>
      ) : (
        <span className="text-ink-faint">—</span>
      );
    return (
      <SheetRow
        key={row.key}
        onClick={firstItem ? () => onOpenItem(firstItem.id) : undefined}
      >
        <SheetCell>
          <div className="font-medium">
            {row.titleShorts ? (
              <>
                {row.titleRoot} <span className="t-ident">{row.titleShorts.join(' → ')}</span>
              </>
            ) : (
              row.titleRoot
            )}
          </div>
          {row.titleShorts && (
            <div className="t-caption text-ink-soft">one account, renumbered</div>
          )}
          {row.citations.length > 0 && (
            <div className="t-caption text-ink-faint">{row.citations.join(' · ')}</div>
          )}
          {unlinked && (
            <div className="t-caption text-ink-faint">
              {blanket
                ? `RFP ${blanket.round.round_number} #${blanket.item.item_number} · blanket request`
                : 'not linked to a request'}{' '}
              <AddToRequestLink
                caseId={caseId}
                party={party}
                bankLabel={row.bankLabel}
                label={blanket ? 'link for exact tracking…' : 'add to request…'}
              />
            </div>
          )}
        </SheetCell>
        <SheetCell>
          {firstItem ? (
            <BatesQuickEdit caseId={caseId} item={firstItem}>
              {produced}
            </BatesQuickEdit>
          ) : (
            produced
          )}
        </SheetCell>
        <SheetCell>
          <MissingCellContent
            caseId={caseId}
            missing={row.missing}
            missingNotes={row.missingNotes}
            last4s={row.last4s}
          />
        </SheetCell>
      </SheetRow>
    );
  };

  return (
    <SheetSection title="Accounts">
      <SheetTable
        caption={`Accounts demanded from ${party.party_label}`}
        columns={SUBJECT_ACCOUNT_COLUMNS}
      >
        {shown.map(renderRow)}
        {hidden.length > 0 && (
          <CollapsedLine
            colSpan={3}
            open={showAll}
            onToggle={() => setShowAll((v) => !v)}
            label={`${hidden.length} account${hidden.length === 1 ? '' : 's'} with nothing outstanding`}
          />
        )}
      </SheetTable>
    </SheetSection>
  );
}

function EverythingElseSection({
  caseId,
  party,
  onOpenItem,
}: {
  caseId: string;
  party: TrackerParty;
  onOpenItem: (itemId: number) => void;
}) {
  const [showAll, setShowAll] = useState(false);
  const { rows, collapsedRows } = splitEverythingElse(party);
  if (rows.length === 0 && collapsedRows.length === 0) return null;
  const shown = showAll ? [...rows, ...collapsedRows] : rows;

  const renderRow = ({ round, item }: ItemRow) => {
    const stateText = stateSheetText(item);
    const isPointer = item.response_state === 'refer' || item.response_state === 'inspection';
    return (
      <SheetRow key={item.id} onClick={() => onOpenItem(item.id)}>
        <SheetCell>
          <div>{item.description}</div>
          <div className="t-caption text-ink-faint">
            RFP {round.round_number} #{item.item_number}
            {item.demand_start && (
              <>
                {' · from '}
                <DateText value={item.demand_start} />
              </>
            )}
            {item.demand_end && (
              <>
                {' through '}
                <DateText value={item.demand_end} />
              </>
            )}
          </div>
        </SheetCell>
        <SheetCell>
          <BatesQuickEdit caseId={caseId} item={item}>
            {item.events.length > 0 ? (
              <EventLines events={item.events} withRanges />
            ) : stateText ? (
              <span className="italic text-ink-soft">{stateText}</span>
            ) : (
              <span className="text-ink-faint">—</span>
            )}
          </BatesQuickEdit>
        </SheetCell>
        <SheetCell>
          {isPointer ? (
            <span className="text-ink-faint">—</span>
          ) : (
            <MissingCellContent
              caseId={caseId}
              missing={item.missing}
              missingNotes={item.missing_note ? [item.missing_note] : []}
              last4s={[]}
            />
          )}
        </SheetCell>
      </SheetRow>
    );
  };

  return (
    <SheetSection title="Everything else demanded">
      <SheetTable
        caption={`Non-account items demanded from ${party.party_label}`}
        columns={SUBJECT_ITEM_COLUMNS}
      >
        {shown.map(renderRow)}
        {collapsedRows.length > 0 && (
          <CollapsedLine
            colSpan={3}
            open={showAll}
            onToggle={() => setShowAll((v) => !v)}
            label={`${collapsedRows.length} more item${collapsedRows.length === 1 ? '' : 's'} — answered "none" or nothing recorded`}
          />
        )}
      </SheetTable>
    </SheetSection>
  );
}

// ── by-request view ──────────────────────────────────────────────────────────

const REQUEST_COLUMNS: SheetColumn[] = [
  { key: 'date', label: 'Date', width: 'w-24' },
  { key: 'number', label: '#', width: 'w-10' },
  { key: 'description', label: 'Description' },
  { key: 'bates', label: 'Bates #', width: 'w-32' },
  { key: 'missing', label: 'Missing', width: 'w-56' },
];

/** Round footer: add an item inline (screen-only). */
function AddItemLine({ caseId, round }: { caseId: string; round: TrackerRound }) {
  const createItem = useCreateItem(caseId);
  const [desc, setDesc] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const nextNumber = round.items.reduce((m, i) => Math.max(m, i.item_number), 0) + 1;

  const submit = () => {
    const description = desc.trim();
    if (!description) return;
    createItem.mutate(
      { roundId: round.id, body: { description } },
      {
        onSuccess: () => {
          setDesc('');
          setErr(null);
        },
        onError: (e) => setErr(extractAxiosDetail(e)),
      },
    );
  };

  return (
    <SheetRow data-print="hide">
      <SheetCell colSpan={REQUEST_COLUMNS.length} className="border-b-0">
        <div className="flex items-center gap-2">
          <span className="t-ident w-8 shrink-0 text-right text-ink-faint">{nextNumber}</span>
          <div className="w-80">
            <Input
              size="sm"
              placeholder="Add an item — description"
              aria-label="New item description"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') submit();
              }}
            />
          </div>
          <Button
            size="sm"
            variant="ghost"
            icon={Plus}
            loading={createItem.isPending}
            disabled={!desc.trim()}
            onClick={submit}
          >
            Add item
          </Button>
        </div>
        {err && <div className={`mt-2 ${ERROR_BANNER}`}>{err}</div>}
      </SheetCell>
    </SheetRow>
  );
}

function RoundSection({
  caseId,
  round,
  party,
  partyName,
  onOpenItem,
}: {
  caseId: string;
  round: TrackerRound;
  party: TrackerParty;
  partyName: string;
  onOpenItem: (itemId: number) => void;
}) {
  const deleteRound = useDeleteRound(caseId);
  const [showAll, setShowAll] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const items = [...round.items].sort((a, b) => a.item_number - b.item_number);
  const quiet = (item: TrackerItem) =>
    item.response_state === null &&
    item.events.length === 0 &&
    !item.missing_note &&
    !(item.missing && !item.missing.complete && item.missing.display);
  const visible = items.filter((i) => !quiet(i));
  const hidden = items.filter(quiet);
  const shown = showAll ? items : visible;

  const spans = round.bates.families.map((f) => (f.prefix ? `${f.prefix} ${f.span}` : f.span));
  const unnumbered = round.bates.unnumbered_documents;

  const renderRow = (item: TrackerItem) => {
    const stateText = stateSheetText(item);
    const linkedShorts = item.accounts.map((a) =>
      a.resolved
        ? (a.chain_labels ?? [a.current_bank_label]).map((l) => shortLabel(l)).join(' → ')
        : `${a.bank_label ?? 'unknown'} — not on file`,
    );
    const ranges = item.events.filter((e) => e.range);
    const isPointer = item.response_state === 'refer' || item.response_state === 'inspection';
    return (
      <SheetRow key={item.id} onClick={() => onOpenItem(item.id)}>
        <SheetCell>
          {item.events.length > 0 ? <EventLines events={item.events} withRanges={false} /> : null}
        </SheetCell>
        <SheetCell tone="ident">{item.item_number}</SheetCell>
        <SheetCell>
          <div>{item.description}</div>
          {linkedShorts.length > 0 && (
            <div className="t-caption text-ink-soft">
              <span className="t-ident">{linkedShorts.join(' · ')}</span>
            </div>
          )}
        </SheetCell>
        <SheetCell>
          <BatesQuickEdit caseId={caseId} item={item}>
            {ranges.length > 0 ? (
              <div className="space-y-1">
                {ranges.map((e, i) => (
                  <div key={i} className="t-ident">
                    {e.range}
                  </div>
                ))}
              </div>
            ) : stateText ? (
              // Response-state text folds INTO the Bates cell — the firm's idiom.
              <span className="italic text-ink-soft">{stateText}</span>
            ) : (
              <span>{' '}</span>
            )}
          </BatesQuickEdit>
        </SheetCell>
        <SheetCell>
          {isPointer ? (
            <span className="text-ink-faint">—</span>
          ) : (
            <MissingCellContent
              caseId={caseId}
              missing={item.missing}
              missingNotes={item.missing_note ? [item.missing_note] : []}
              last4s={Array.from(
                new Set(
                  item.accounts
                    .filter((a) => a.resolved)
                    .flatMap((a) => a.chain_last4s ?? (a.last4 ? [a.last4] : [])),
                ),
              )}
            />
          )}
        </SheetCell>
      </SheetRow>
    );
  };

  return (
    <SheetSection
      data-round-id={round.id}
      title={
        <span className="inline-flex flex-wrap items-center gap-2">
          <span>{roundTitle(round)}</span>
          {round.response_date && (
            <span className="text-ink-faint">
              — responses <DateText value={round.response_date} />
            </span>
          )}
          {spans.length > 0 && (
            <span className="text-ink-faint">
              · Bates <span className="t-ident">{spans.join(', ')}</span>
            </span>
          )}
        </span>
      }
      end={
        <span className="inline-flex items-center gap-1" data-print="hide">
          <IconButton
            icon={Pencil}
            aria-label={`Edit ${roundTitle(round)}`}
            size="xs"
            onClick={() => setEditOpen(true)}
          />
          <IconButton
            icon={Trash2}
            aria-label={`Delete ${roundTitle(round)}`}
            size="xs"
            variant="danger-ghost"
            onClick={() => setConfirmDelete(true)}
          />
        </span>
      }
    >
      {round.bates.families
        .filter((f) => f.missing_line)
        .map((f) => (
          <p
            key={f.prefix || 'bare'}
            className="t-caption mb-1 text-danger"
            title="Bates numbers inside this round's span that no recorded page carries"
          >
            {f.missing_line}
            {f.padding_drift && ' · padding drift'}
          </p>
        ))}
      {round.bates.conflicts.map((c) => (
        <p key={c} className="t-caption mb-1 text-danger">
          {c}
        </p>
      ))}
      {unnumbered.length > 0 && (
        <p className="t-caption mb-1 italic text-ink-soft">
          {unnumbered.length} document(s) with no recorded Bates numbers (
          {unnumbered.map((d) => d.filename).join(', ')}) — MISSING runs may include their pages
        </p>
      )}
      {err && <div className={`mb-2 ${ERROR_BANNER}`}>{err}</div>}
      <SheetTable
        caption={`${roundTitle(round)} items — ${partyName}`}
        columns={REQUEST_COLUMNS}
      >
        {shown.map(renderRow)}
        {hidden.length > 0 && (
          <CollapsedLine
            colSpan={REQUEST_COLUMNS.length}
            open={showAll}
            onToggle={() => setShowAll((v) => !v)}
            label={`${hidden.length} more item${hidden.length === 1 ? '' : 's'} — nothing recorded yet`}
          />
        )}
        <AddItemLine caseId={caseId} round={round} />
      </SheetTable>

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteRound.mutate(round.id, {
            onSuccess: () => setConfirmDelete(false),
            onError: (e) => {
              setConfirmDelete(false);
              setErr(extractAxiosDetail(e));
            },
          })
        }
        title={`Delete ${roundTitle(round)}?`}
        message="Deleting the round removes its items and document assignments; documents stay in the case."
        confirmLabel="Delete round"
        variant="danger"
        loading={deleteRound.isPending}
      />
      {editOpen && (
        <RoundModal
          caseId={caseId}
          party={party.producing_party}
          partyName={partyName}
          partyRoundCount={party.rounds.length}
          round={round}
          onClose={() => setEditOpen(false)}
        />
      )}
    </SheetSection>
  );
}

// ── heading chrome: inline anchor, round meta chips ──────────────────────────

/** The case-level "Time requested from" date, edited in place (dashed
 * underline → date input, commit on blur). */
function InlineAnchor({ caseId, anchor }: { caseId: string; anchor: string | null }) {
  const updateCase = useUpdateCase();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const cancelled = useRef(false);

  const commit = (raw: string) => {
    if (cancelled.current) {
      cancelled.current = false;
      return;
    }
    setEditing(false);
    if ((raw || null) === anchor) return;
    updateCase.mutate(
      { id: caseId, updates: { discovery_requested_from: raw || null } },
      {
        onSuccess: () => {
          setErr(null);
          queryClient.invalidateQueries({ queryKey: ['production-tracker', caseId] });
          queryClient.invalidateQueries({ queryKey: ['missing-gaps', caseId] });
          queryClient.invalidateQueries({ queryKey: ['cases', 'by-public-id', caseId] });
        },
        onError: (e) => setErr(extractAxiosDetail(e)),
      },
    );
  };

  if (editing) {
    return (
      <span className="inline-block w-40 align-middle">
        <Input
          type="date"
          size="sm"
          autoFocus
          aria-label="Discovery requested from"
          defaultValue={anchor ?? ''}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            if (e.key === 'Escape') {
              cancelled.current = true;
              setEditing(false);
            }
          }}
          onBlur={(e) => commit(e.target.value)}
        />
      </span>
    );
  }
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto px-1 py-0 text-ink underline decoration-dashed underline-offset-4"
        title="Time requested from — items may override with their own window."
        onClick={() => {
          cancelled.current = false;
          setEditing(true);
        }}
      >
        {anchor ? <DateText value={anchor} /> : 'set date'}
      </Button>
      {!anchor && (
        <span className="t-caption text-ink-faint">— set the anchor to compute MISSING</span>
      )}
      {err && <span className="t-caption text-danger">{err}</span>}
    </>
  );
}

/** One "RFP 1 · 11/25/2025" chip on the rounds metadata line — click for the
 * quick popover (dates, method, assign documents, delete). */
function RoundMetaChip({
  caseId,
  round,
  party,
  partyName,
}: {
  caseId: string;
  round: TrackerRound;
  party: TrackerParty;
  partyName: string;
}) {
  const patchRound = usePatchRound(caseId);
  const deleteRound = useDeleteRound(caseId);
  const [open, setOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const patch = (body: Parameters<typeof patchRound.mutate>[0]['body']) =>
    patchRound.mutate(
      { roundId: round.id, body },
      { onSuccess: () => setErr(null), onError: (e) => setErr(extractAxiosDetail(e)) },
    );

  const chipDate = round.response_date ?? round.served_date;
  return (
    <span className="relative inline-flex">
      <Button
        variant="ghost"
        size="sm"
        className="h-auto gap-1 px-1 py-0 text-ink-soft"
        onClick={() => setOpen((v) => !v)}
      >
        {roundTitle(round)}
        {chipDate && (
          <>
            {' · '}
            <DateText value={chipDate} />
          </>
        )}
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-full z-20 mt-1 w-64 rounded-overlay border border-border bg-cream-light p-3 shadow-overlay">
            <div className="space-y-2">
              <DraftInput
                label="Served"
                type="date"
                size="sm"
                value={round.served_date ?? ''}
                onCommit={(raw) => patch({ served_date: raw || null })}
              />
              <DraftInput
                label="Responses"
                type="date"
                size="sm"
                value={round.response_date ?? ''}
                onCommit={(raw) => patch({ response_date: raw || null })}
              />
              <Select
                label="Method"
                size="sm"
                options={METHOD_OPTIONS}
                value={round.method}
                onChange={(e) => patch({ method: e.target.value })}
              />
              <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-2">
                <Button size="sm" variant="secondary" onClick={() => setAttachOpen(true)}>
                  Assign documents
                </Button>
                <Button
                  size="sm"
                  variant="danger-ghost"
                  icon={Trash2}
                  onClick={() => setConfirmDelete(true)}
                >
                  Delete
                </Button>
              </div>
              {err && <div className={ERROR_BANNER}>{err}</div>}
            </div>
          </div>
        </>
      )}
      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteRound.mutate(round.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              setOpen(false);
            },
            onError: (e) => {
              setConfirmDelete(false);
              setErr(extractAxiosDetail(e));
            },
          })
        }
        title={`Delete ${roundTitle(round)}?`}
        message="Deleting the round removes its items and document assignments; documents stay in the case."
        confirmLabel="Delete round"
        variant="danger"
        loading={deleteRound.isPending}
      />
      {attachOpen && (
        <AttachDocumentsModal
          caseId={caseId}
          round={round}
          party={party.producing_party}
          partyName={partyName}
          onClose={() => setAttachOpen(false)}
        />
      )}
    </span>
  );
}

// ── RowDrawer (the item editor — replaces the old expanded-row detail) ───────

function RowDrawerBody({
  caseId,
  party,
  round,
  item,
  partyName,
  accounts,
  onDeleted,
}: {
  caseId: string;
  party: TrackerParty;
  round: TrackerRound;
  item: TrackerItem;
  partyName: string;
  accounts: AccountData[];
  onDeleted: () => void;
}) {
  const patchItem = usePatchItem(caseId);
  const deleteItem = useDeleteItem(caseId);
  const setItemAccounts = useSetItemAccounts(caseId);
  const setItemDocs = useSetItemDocuments(caseId);
  const [err, setErr] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);

  const onError = (e: unknown) => setErr(extractAxiosDetail(e));
  const patch = (body: Parameters<typeof patchItem.mutate>[0]['body']) =>
    patchItem.mutate({ itemId: item.id, body }, { onSuccess: () => setErr(null), onError });

  const linkedLabels = item.accounts
    .filter((a) => a.resolved && a.current_bank_label)
    .map((a) => a.current_bank_label as string);
  const unresolvedLinks = item.accounts.filter((a) => !a.resolved);
  // Unresolved stored links ride EVERY replace-set PUT (the server preserves
  // their stored snapshot) — an unrelated checkbox toggle must never silently
  // delete a "not on file" link; only its explicit X removes it.
  const unresolvedLabels = unresolvedLinks
    .map((a) => a.bank_label)
    .filter((l): l is string => !!l);

  const putAccounts = (labels: string[]) => {
    setItemAccounts.mutate(
      { itemId: item.id, bankLabels: Array.from(new Set(labels)) },
      { onSuccess: () => setErr(null), onError },
    );
  };

  const toggleAccount = (bankLabel: string, checked: boolean) => {
    const base = [...linkedLabels, ...unresolvedLabels];
    putAccounts(checked ? [...base, bankLabel] : base.filter((l) => l !== bankLabel));
  };

  const removeUnresolved = (bankLabel: string) => {
    putAccounts([...linkedLabels, ...unresolvedLabels.filter((l) => l !== bankLabel)]);
  };

  const unlinkDocument = (docId: number) => {
    setItemDocs.mutate(
      {
        itemId: item.id,
        documentIds: item.documents.map((d) => d.document_id).filter((id) => id !== docId),
      },
      { onSuccess: () => setErr(null), onError },
    );
  };

  const maxSupplement = round.supplements.reduce((m, s) => Math.max(m, s.supplement), 0);

  return (
    <div className="space-y-4">
      {err && <div className={ERROR_BANNER}>{err}</div>}

      <div>
        <p className="t-body">{item.description}</p>
        <p className="t-caption mt-1 text-ink-faint">
          {roundTitle(round)} #{item.item_number} — {partyName}
        </p>
      </div>

      {/* Response */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <Select
            label="Response"
            size="sm"
            options={RESPONSE_STATE_OPTIONS}
            value={item.response_state ?? ''}
            onChange={(e) => patch({ response_state: e.target.value || null })}
          />
        </div>
        <div className="w-56">
          <DraftInput
            label={item.response_state === 'inspection' ? 'Location' : 'Refer to'}
            size="sm"
            placeholder={
              item.response_state === 'inspection'
                ? 'e.g. marital residence'
                : 'e.g. refer to RFP 2'
            }
            value={item.refer_to ?? ''}
            onCommit={(raw) => patch({ refer_to: raw || null })}
          />
        </div>
      </div>

      {/* Typed Bates cell */}
      <BatesRangesEditor caseId={caseId} item={item} maxSupplement={maxSupplement} />

      {/* Demanded window */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="w-44">
          <DraftInput
            label="Demanded from"
            type="date"
            size="sm"
            value={item.demand_start ?? ''}
            onCommit={(raw) => patch({ demand_start: raw || null })}
            hint="Blank = case anchor"
          />
        </div>
        <div className="w-44">
          <DraftInput
            label="Through"
            type="date"
            size="sm"
            value={item.demand_end ?? ''}
            onCommit={(raw) => patch({ demand_end: raw || null })}
            hint="Blank = current (193.5)"
          />
        </div>
      </div>

      {/* Account links */}
      <div className="space-y-2">
        <h4 className="t-label text-ink-soft">Linked accounts</h4>
        {accounts.length === 0 ? (
          <p className="t-caption text-ink-faint">No accounts on file yet.</p>
        ) : (
          <ul className="space-y-1">
            {accounts.map((acc) => (
              <li key={acc.bank_label}>
                <label className="flex cursor-pointer items-center gap-2">
                  {/* eslint-disable-next-line no-restricted-syntax -- native checkbox: no ui/Checkbox primitive exists yet */}
                  <input
                    type="checkbox"
                    className="size-4 shrink-0 accent-green-700"
                    checked={linkedLabels.includes(acc.bank_label)}
                    onChange={(e) => toggleAccount(acc.bank_label, e.target.checked)}
                  />
                  <span className="min-w-0 truncate text-sm">{acc.bank_label}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
        {unresolvedLinks.map((a) => (
          <div key={a.bank_label ?? 'unknown'} className="flex items-center gap-1">
            <Tag tone="warn" size="sm">
              {a.bank_label ?? 'Unknown account'} — not on file, relink
            </Tag>
            {a.bank_label && (
              <IconButton
                icon={X}
                aria-label={`Remove link ${a.bank_label}`}
                size="xs"
                onClick={() => removeUnresolved(a.bank_label as string)}
              />
            )}
          </div>
        ))}
      </div>

      {/* Document links */}
      <div className="space-y-2">
        <h4 className="t-label text-ink-soft">Linked documents</h4>
        {item.documents.length === 0 ? (
          <p className="t-caption text-ink-faint">No documents linked to this item.</p>
        ) : (
          <ul className="space-y-1">
            {item.documents.map((doc) => (
              <li key={doc.document_id} className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-sm">{doc.filename}</span>
                {doc.bates_range && (
                  <Tag tone="neutral" size="sm">
                    <span className="t-ident">{doc.bates_range}</span>
                  </Tag>
                )}
                {doc.supplement > 0 && (
                  <span className="t-caption text-ink-faint">{suppLabel(doc.supplement)}</span>
                )}
                <IconButton
                  icon={X}
                  aria-label={`Unlink ${doc.filename}`}
                  size="xs"
                  onClick={() => unlinkDocument(doc.document_id)}
                />
              </li>
            ))}
          </ul>
        )}
        <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAttachOpen(true)}>
          Attach documents
        </Button>
      </div>

      <DraftTextArea
        label="MISSING notes"
        hint="Attorney-authored demands appended to the computed MISSING cell."
        rows={2}
        value={item.missing_note}
        onCommit={(raw) => patch({ missing_note: raw || null })}
      />
      <DraftTextArea
        label="Notes (work product)"
        rows={2}
        value={item.notes}
        onCommit={(raw) => patch({ notes: raw || null })}
      />

      <div className="flex justify-end">
        <Button
          size="sm"
          variant="danger-ghost"
          icon={Trash2}
          onClick={() => setConfirmDelete(true)}
        >
          Delete item
        </Button>
      </div>

      <ConfirmModal
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() =>
          deleteItem.mutate(item.id, {
            onSuccess: () => {
              setConfirmDelete(false);
              onDeleted();
            },
            onError: (e) => {
              setConfirmDelete(false);
              onError(e);
            },
          })
        }
        title="Delete this item?"
        message="Deleting the item removes its typed Bates entries and its account and document links. Documents stay in the case."
        confirmLabel="Delete item"
        variant="danger"
        loading={deleteItem.isPending}
      />

      {attachOpen && (
        <AttachDocumentsModal
          caseId={caseId}
          round={round}
          party={party.producing_party}
          partyName={partyName}
          itemId={item.id}
          itemDocIds={item.documents.map((d) => d.document_id)}
          onClose={() => setAttachOpen(false)}
        />
      )}
    </div>
  );
}

// ── ChainsDrawer (the old ChainsCard content, moved — not redesigned) ────────

function ChainsDrawer({
  caseId,
  tracker,
  accounts,
  open,
  onClose,
}: {
  caseId: string;
  tracker: ProductionTracker;
  accounts: AccountData[];
  open: boolean;
  onClose: () => void;
}) {
  const createSucc = useCreateSuccession(caseId);
  const deleteSucc = useDeleteSuccession(caseId);
  const [pred, setPred] = useState('');
  const [succ, setSucc] = useState('');
  const [seamDate, setSeamDate] = useState('');
  const [reason, setReason] = useState('reissue');
  const [err, setErr] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<number | null>(null);

  const labelOptions: SelectOption[] = accounts.map((a) => ({
    value: a.bank_label,
    label: a.bank_label,
  }));

  const submit = () => {
    createSucc.mutate(
      {
        predecessor_bank_label: pred,
        successor_bank_label: succ,
        succession_date: seamDate || null,
        reason,
      },
      {
        onSuccess: () => {
          setPred('');
          setSucc('');
          setSeamDate('');
          setReason('reissue');
          setErr(null);
        },
        onError: (e) => setErr(extractAxiosDetail(e)),
      },
    );
  };

  const multiChains = tracker.chains.filter((c) => c.labels.length >= 2);
  const unresolvedNotes = tracker.chains.flatMap((c) => c.unresolved ?? []);

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Account succession chains"
      description="Reissued or renumbered accounts — the chain, not the account, carries one continuous demanded period."
      size="md"
    >
      <div className="space-y-3">
        {multiChains.map((chain) => (
          <div key={chain.root} className="flex flex-wrap items-center gap-2">
            {chain.labels.map((l, i) => (
              <Fragment key={l}>
                {i > 0 && <span className="text-ink-faint">→</span>}
                <span className="t-ident">{l}</span>
              </Fragment>
            ))}
            {chain.edges
              .filter((e) => e.succession_date)
              .map((e) => (
                <Tag
                  key={`${e.predecessor}-${e.successor}`}
                  tone="neutral"
                  size="sm"
                  title={`${e.predecessor} → ${e.successor} (${REASON_LABELS[e.reason] ?? e.reason})`}
                >
                  <DateText value={e.succession_date} />
                </Tag>
              ))}
          </div>
        ))}
        {unresolvedNotes.map((n) => (
          <Tag key={n} tone="warn" size="sm" title={n}>
            unresolved link — relink
          </Tag>
        ))}

        {tracker.successions.length === 0 ? (
          <p className="t-caption text-ink-faint">
            No succession links yet. Record one when an account was reissued under a new number or
            moved banks.
          </p>
        ) : (
          <ul className="space-y-1">
            {tracker.successions.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-2">
                <span className="t-ident min-w-0 truncate">{s.predecessor_bank_label}</span>
                <span className="text-ink-faint">→</span>
                <span className="t-ident min-w-0 truncate">{s.successor_bank_label}</span>
                {s.succession_date && (
                  <Tag tone="neutral" size="sm">
                    <DateText value={s.succession_date} />
                  </Tag>
                )}
                <Tag tone="info" size="sm">
                  {REASON_LABELS[s.reason] ?? s.reason}
                </Tag>
                {!s.resolved && (
                  <Tag tone="warn" size="sm">
                    not on file — relink
                  </Tag>
                )}
                <IconButton
                  icon={Trash2}
                  aria-label={`Remove link ${s.predecessor_bank_label} to ${s.successor_bank_label}`}
                  size="xs"
                  variant="danger-ghost"
                  onClick={() => setConfirmId(s.id)}
                />
              </li>
            ))}
          </ul>
        )}

        <div className="space-y-2 border-t border-border-subtle pt-3">
          <Select
            label="Predecessor"
            size="sm"
            options={labelOptions}
            placeholder="Old account"
            value={pred}
            onChange={(e) => setPred(e.target.value)}
          />
          <Select
            label="Successor"
            size="sm"
            options={labelOptions}
            placeholder="New account"
            value={succ}
            onChange={(e) => setSucc(e.target.value)}
          />
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-40">
              <Input
                label="Changed on"
                size="sm"
                type="date"
                value={seamDate}
                onChange={(e) => setSeamDate(e.target.value)}
              />
            </div>
            <div className="w-56">
              <Select
                label="Reason"
                size="sm"
                options={REASON_OPTIONS}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>
          <Button
            size="sm"
            variant="secondary"
            icon={Plus}
            disabled={!pred || !succ}
            loading={createSucc.isPending}
            onClick={submit}
          >
            Add link
          </Button>
          {err && <div className={ERROR_BANNER}>{err}</div>}
        </div>
      </div>

      <ConfirmModal
        open={confirmId !== null}
        onClose={() => setConfirmId(null)}
        onConfirm={() =>
          confirmId !== null &&
          deleteSucc.mutate(confirmId, {
            onSuccess: () => setConfirmId(null),
            onError: (e) => {
              setConfirmId(null);
              setErr(extractAxiosDetail(e));
            },
          })
        }
        title="Remove this succession link?"
        message="Removing the link splits the chain — coverage and gap math go back to treating the two accounts separately."
        confirmLabel="Remove link"
        variant="danger"
        loading={deleteSucc.isPending}
      />
    </Drawer>
  );
}

// ── toolbar chrome ───────────────────────────────────────────────────────────

/** Download popover: discovery-safe by default; the work-product file is an
 * explicit second choice stamped DO NOT PRODUCE. */
function DownloadMenu({
  caseId,
  clientName,
  onError,
}: {
  caseId: string;
  clientName?: string | null;
  onError: (msg: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const run = async (includeWorkProduct: boolean) => {
    setOpen(false);
    if (downloading) return;
    setDownloading(true);
    onError(null);
    try {
      const params: Record<string, string> = {};
      if (includeWorkProduct) params.include_work_product = 'true';
      // Filename mirrors the server convention:
      // shale_{client}_production_tracker_{yyyymmdd}{_WORKPRODUCT}.xlsx
      const safeClient = (clientName ?? 'case').replace(/[^A-Za-z0-9\-_]/g, '') || 'case';
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const tag = includeWorkProduct ? '_WORKPRODUCT' : '';
      await downloadTrackerBlob(
        `/cases/${caseId}/production-tracker.xlsx`,
        params,
        `shale_${safeClient}_production_tracker_${stamp}${tag}.xlsx`,
      );
    } catch (err) {
      onError(extractAxiosDetail(err));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="secondary"
        size="sm"
        icon={Download}
        iconTrailing={ChevronDown}
        loading={downloading}
        onClick={() => setOpen((v) => !v)}
      >
        Download
      </Button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-20 mt-1 w-80 rounded-overlay border border-border bg-cream-light py-1 shadow-overlay">
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              className="justify-start px-3"
              onClick={() => run(false)}
            >
              Download (discovery-safe)
            </Button>
            <Button
              variant="ghost"
              size="sm"
              fullWidth
              className="justify-start gap-2 px-3"
              onClick={() => run(true)}
            >
              Download with notes — work product
              <Tag size="sm" tone="danger">
                DO NOT PRODUCE
              </Tag>
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/** Footer line for a party with no rounds yet. */
function RoundlessFooter({
  caseId,
  party,
  partyName,
}: {
  caseId: string;
  party: ProducingParty;
  partyName: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <p className="t-caption text-ink-faint" data-print="hide">
        Production by {partyName} — no rounds yet ·{' '}
        <Button
          variant="ghost"
          size="sm"
          className="h-auto px-1 py-0 text-ink-soft underline decoration-dashed underline-offset-4"
          onClick={() => setOpen(true)}
        >
          add round
        </Button>
      </p>
      {open && (
        <RoundModal
          caseId={caseId}
          party={party}
          partyName={partyName}
          partyRoundCount={0}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// ── the party sheet ──────────────────────────────────────────────────────────

function PartySheet({
  caseId,
  tracker,
  party,
  partyName,
  view,
  isFirst,
  accountRows,
  onOpenItem,
}: {
  caseId: string;
  tracker: ProductionTracker;
  party: TrackerParty;
  partyName: string;
  view: 'subject' | 'request';
  isFirst: boolean;
  accountRows: AccountRow[];
  onOpenItem: (itemId: number) => void;
}) {
  const [addRoundOpen, setAddRoundOpen] = useState(false);
  const [fileDocsOpen, setFileDocsOpen] = useState(false);

  const unassigned = tracker.unassigned_documents.filter(
    (d) => d.source_party === party.producing_party,
  );
  const latestRound = party.rounds[party.rounds.length - 1];

  return (
    <Sheet data-party-sheet={party.producing_party}>
      <SheetHeading
        title={`Production by ${partyName}`}
        meta={
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {/* The anchor is CASE-level — it appears once, on the first sheet. */}
              {isFirst && (
                <span className="inline-flex items-center gap-1">
                  Time requested from <InlineAnchor caseId={caseId} anchor={tracker.anchor} />
                </span>
              )}
              <span className="inline-flex flex-wrap items-center gap-2">
                <span>rounds:</span>
                {party.rounds.map((round) => (
                  <RoundMetaChip
                    key={round.id}
                    caseId={caseId}
                    round={round}
                    party={party}
                    partyName={partyName}
                  />
                ))}
                <span data-print="hide">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-auto px-1 py-0 text-ink-soft"
                    onClick={() => setAddRoundOpen(true)}
                  >
                    + add
                  </Button>
                </span>
              </span>
            </div>
            {unassigned.length > 0 && latestRound && (
              <div data-print="hide">
                {unassigned.length} document{unassigned.length === 1 ? '' : 's'} not filed to a
                round ·{' '}
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-auto px-1 py-0 text-ink-soft underline decoration-dashed underline-offset-4"
                  onClick={() => setFileDocsOpen(true)}
                >
                  file them
                </Button>
              </div>
            )}
          </div>
        }
      />

      {view === 'subject' ? (
        <>
          <AccountsSection
            caseId={caseId}
            party={party}
            rows={accountRows}
            onOpenItem={onOpenItem}
          />
          <EverythingElseSection caseId={caseId} party={party} onOpenItem={onOpenItem} />
        </>
      ) : (
        party.rounds.map((round) => (
          <RoundSection
            key={round.id}
            caseId={caseId}
            round={round}
            party={party}
            partyName={partyName}
            onOpenItem={onOpenItem}
          />
        ))
      )}

      {addRoundOpen && (
        <RoundModal
          caseId={caseId}
          party={party.producing_party}
          partyName={partyName}
          partyRoundCount={party.rounds.length}
          onClose={() => setAddRoundOpen(false)}
        />
      )}
      {fileDocsOpen && latestRound && (
        <AttachDocumentsModal
          caseId={caseId}
          round={latestRound}
          party={party.producing_party}
          partyName={partyName}
          onClose={() => setFileDocsOpen(false)}
        />
      )}
    </Sheet>
  );
}

// ── the tab ──────────────────────────────────────────────────────────────────

function findItemCtx(
  tracker: ProductionTracker,
  itemId: number,
): { party: TrackerParty; round: TrackerRound; item: TrackerItem } | null {
  for (const party of tracker.parties) {
    for (const round of party.rounds) {
      const item = round.items.find((i) => i.id === itemId);
      if (item) return { party, round, item };
    }
  }
  return null;
}

interface Props {
  caseId: string;
  clientName?: string | null;
  opposingPartyName?: string | null;
}

export default function ProductionTrackerTab({ caseId, clientName, opposingPartyName }: Props) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const trackerQ = useProductionTracker(caseId);
  const accountsQ = useAccounts(caseId);
  const gapsQ = useMissingStatementGaps(caseId);
  const tracker = trackerQ.data;
  const accounts = useMemo(() => accountsQ.data?.accounts ?? [], [accountsQ.data]);
  const successions = useMemo(() => accountsQ.data?.successions ?? [], [accountsQ.data]);
  const gaps = useMemo(() => gapsQ.data?.gaps ?? [], [gapsQ.data]);

  const roundParam = searchParams.get('round');
  const roundIdParam = roundParam ? Number(roundParam) : null;

  // A ?round= deep link lands on the classic per-round layout.
  const [view, setView] = useState<'subject' | 'request'>(
    roundIdParam !== null ? 'request' : 'subject',
  );
  const [rowItemId, setRowItemId] = useState<number | null>(null);
  const [chainsOpen, setChainsOpen] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const accountRows = useMemo(
    () =>
      tracker
        ? buildAccountRows(tracker, accounts, successions, gaps)
        : new Map<ProducingParty, AccountRow[]>(),
    [tracker, accounts, successions, gaps],
  );

  // The drawer's item is derived fresh from the aggregate on every refetch.
  const rowCtx = tracker && rowItemId !== null ? findItemCtx(tracker, rowItemId) : null;

  // Deep-link scroll: ?round= scrolls to the round section (by-request view),
  // ?party= to the party's sheet.
  const scrolled = useRef(false);
  useEffect(() => {
    if (scrolled.current || !tracker) return;
    const partyParam = searchParams.get('party');
    if (roundIdParam === null && !partyParam) return;
    scrolled.current = true;
    window.setTimeout(() => {
      const el =
        roundIdParam !== null
          ? document.querySelector(`[data-round-id="${roundIdParam}"]`)
          : document.querySelector(`[data-party-sheet="${partyParam}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, [tracker, roundIdParam, searchParams]);

  if (trackerQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton variant="rect" width="100%" height={72} />
        <SkeletonTable rows={6} columns={4} />
      </div>
    );
  }

  if (trackerQ.error || !tracker) {
    return (
      <Card>
        <EmptyState
          title="Could not load the production tracker"
          description={trackerQ.error ? extractAxiosDetail(trackerQ.error) : undefined}
        />
      </Card>
    );
  }

  const partiesWithRounds = PARTY_ORDER.filter(
    (p) => (tracker.parties.find((x) => x.producing_party === p)?.rounds.length ?? 0) > 0,
  );
  const roundless = PARTY_ORDER.filter((p) => !partiesWithRounds.includes(p));
  const firstSheetParty = partiesWithRounds[0] ?? null;

  return (
    <div className="space-y-4">
      {/* Toolbar and its errors are screen chrome — hidden under @media print. */}
      <div data-print="hide">
        <Toolbar
          end={
            <>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/cases/${caseId}/drafts/letters`)}
              >
                Draft demand letter
              </Button>
              <Button
                variant="ghost"
                size="sm"
                data-print="hide"
                onClick={() => setChainsOpen(true)}
              >
                Chains
              </Button>
              <DownloadMenu caseId={caseId} clientName={clientName} onError={setDownloadError} />
              <Button
                variant="secondary"
                size="sm"
                icon={Printer}
                onClick={() => window.print()}
              >
                Print
              </Button>
            </>
          }
        >
          <ToolbarChip selected={view === 'subject'} onClick={() => setView('subject')}>
            By subject
          </ToolbarChip>
          <ToolbarChip selected={view === 'request'} onClick={() => setView('request')}>
            By request
          </ToolbarChip>
        </Toolbar>
        {downloadError && <div className={`mt-2 ${ERROR_BANNER}`}>{downloadError}</div>}
      </div>

      {partiesWithRounds.length === 0 && (
        <p className="t-caption text-ink-soft">
          Time requested from <InlineAnchor caseId={caseId} anchor={tracker.anchor} />
        </p>
      )}

      {partiesWithRounds.map((p) => {
        const party = tracker.parties.find((x) => x.producing_party === p) as TrackerParty;
        return (
          <PartySheet
            key={p}
            caseId={caseId}
            tracker={tracker}
            party={party}
            partyName={partyLabel(p, clientName, opposingPartyName)}
            view={view}
            isFirst={p === firstSheetParty}
            accountRows={accountRows.get(p) ?? []}
            onOpenItem={setRowItemId}
          />
        );
      })}

      {roundless.map((p) => (
        <RoundlessFooter
          key={p}
          caseId={caseId}
          party={p}
          partyName={partyLabel(p, clientName, opposingPartyName)}
        />
      ))}

      <Drawer
        open={rowCtx !== null}
        onClose={() => setRowItemId(null)}
        title={rowCtx ? `${roundTitle(rowCtx.round)} · #${rowCtx.item.item_number}` : 'Item'}
        size="md"
      >
        {rowCtx && (
          <RowDrawerBody
            caseId={caseId}
            party={rowCtx.party}
            round={rowCtx.round}
            item={rowCtx.item}
            partyName={partyLabel(rowCtx.party.producing_party, clientName, opposingPartyName)}
            accounts={accounts}
            onDeleted={() => setRowItemId(null)}
          />
        )}
      </Drawer>

      <ChainsDrawer
        caseId={caseId}
        tracker={tracker}
        accounts={accounts}
        open={chainsOpen}
        onClose={() => setChainsOpen(false)}
      />
    </div>
  );
}
